import { useState } from "react";
import { Crosshair, CalendarDays, Star, MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { AccountStatusBadge, PriorityBadge } from "@/components/targeting/StatusBadge";
import {
  useUpdateAccount,
  useFollowupGroups,
  useAccountFollowupGroups,
  useToggleAccountFollowupGroup,
} from "@/hooks/useTargetUniverse";
import { useAuth } from "@/contexts/AuthContext";
import { useSaveAccountNote } from "@/hooks/useAccountNotes";

interface AccountTargetingCardProps {
  company: Record<string, any>;
}

export function AccountTargetingCard({ company }: AccountTargetingCardProps) {
  const updateAccount = useUpdateAccount();
  const { data: followupGroups } = useFollowupGroups();
  const { data: accountGroups } = useAccountFollowupGroups(company.id);
  const toggleGroup = useToggleAccountFollowupGroup();
  const { userProfile } = useAuth();

  const performedBy = {
    id: userProfile?.id ?? null,
    name: userProfile?.sales_rep_name || userProfile?.full_name || "User",
  };

  const [editing, setEditing] = useState(false);
  const [priority, setPriority] = useState(company.engagement_priority || "");
  const [followupDate, setFollowupDate] = useState(company.next_followup_date || "");
  const [tier, setTier] = useState(company.account_tier || "new");
  const [qualReason, setQualReason] = useState(company.qualification_reason || "");

  const accountGroupIds = new Set((accountGroups || []).map((ag: any) => ag.group_id));

  const handleSave = async () => {
    const oldTier = company.account_tier || "new";

    // Require qualification_reason when disqualifying
    if (tier === "disqualified" && !qualReason.trim()) {
      toast.error("Disqualification reason required", {
        description: "Please explain why this account should be disqualified before saving.",
      });
      return;
    }

    try {
      await updateAccount.mutateAsync({
        accountId: company.id,
        fields: {
          engagement_priority: priority || null,
          next_followup_date: followupDate || null,
          last_reviewed_at: new Date().toISOString(),
          last_reviewed_by: performedBy.name,
          account_tier: tier,
          qualification_reason: qualReason || null,
          ...(oldTier !== tier
            ? { tier_changed_at: new Date().toISOString(), tier_changed_by: performedBy.name }
            : {}),
        },
        activityType: oldTier !== tier ? "tier_changed" : "reviewed",
        activityDescription:
          oldTier !== tier
            ? `Tier changed from ${oldTier} to ${tier}`
            : "Account reviewed from detail page",
        activityMetadata: { old_tier: oldTier, new_tier: tier },
        performedBy,
      });
      toast.success("Targeting updated", { description: `${company.company_name} saved.` });
      setEditing(false);
    } catch {
      toast.error("Failed to update targeting");
    }
  };

  const handleCancel = () => {
    setPriority(company.engagement_priority || "");
    setFollowupDate(company.next_followup_date || "");
    setTier(company.account_tier || "new");
    setQualReason(company.qualification_reason || "");
    setEditing(false);
  };

  const lastReviewed = company.last_reviewed_at
    ? new Date(company.last_reviewed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // ─── READ-ONLY VIEW ───
  if (!editing) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Crosshair className="h-4 w-4" />
              Targeting
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Row 1: Core targeting fields. Account ownership is shown via the read-only
             Sales Rep / Territory rows in AccountOverviewSection — territory drives the rep,
             so it is not editable here. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Account Status</span>
              </div>
              <AccountStatusBadge status={company.account_tier} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Priority</span>
              </div>
              <PriorityBadge priority={company.engagement_priority} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Follow-up</span>
              </div>
              <p className="text-sm font-medium">
                {company.next_followup_date
                  ? new Date(company.next_followup_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : "—"}
              </p>
            </div>
          </div>

          {/* Follow-up Groups */}
          {(accountGroups || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Cadence:</span>
              {(followupGroups || [])
                .filter((g: any) => accountGroupIds.has(g.id))
                .map((g: any) => (
                  <span
                    key={g.id}
                    className="text-xs px-2 py-0.5 rounded-full border"
                    style={{ borderColor: g.color, color: g.color }}
                  >
                    {g.name}
                  </span>
                ))}
            </div>
          )}

          <Separator />

          {/* Notes (read-only view with inline new-note capture) */}
          <NotesSection company={company} userId={performedBy.id} />

          {lastReviewed && (
            <p className="text-xs text-muted-foreground">
              Last reviewed {lastReviewed}
              {company.last_reviewed_by ? ` by ${company.last_reviewed_by}` : ""}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // ─── EDIT VIEW ───
  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Crosshair className="h-4 w-4" />
          Edit Targeting
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          {/* Tier & Priority */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tier & Priority</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Account Status</Label>
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New (Unreviewed)</SelectItem>
                    <SelectItem value="cold_target">Cold Target</SelectItem>
                    <SelectItem value="warm_target">Warm Target</SelectItem>
                    <SelectItem value="active_prospect">Active Prospect</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="disqualified">Disqualified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Set priority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <Separator />

          {/* Follow-up */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Follow-up</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Follow-up Date</Label>
                <Input
                  type="date"
                  value={followupDate}
                  onChange={(e) => setFollowupDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Follow-up Cadence Groups */}
            <div>
              <Label className="text-xs mb-2 block">Follow-up Cadence</Label>
              <div className="flex flex-wrap gap-3">
                {(followupGroups || []).map((group: any) => (
                  <div key={group.id} className="flex items-center gap-1.5">
                    <Checkbox
                      checked={accountGroupIds.has(group.id)}
                      onCheckedChange={(checked) => {
                        toggleGroup.mutate({
                          accountId: company.id,
                          groupId: group.id,
                          action: checked ? "add" : "remove",
                          cadence: group.cadence,
                        });
                      }}
                    />
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                    <span className="text-xs">{group.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Adding a cadence auto-sets the next follow-up date.
              </p>
            </div>
          </section>

          {tier === "disqualified" && (
            <>
              <Separator />
              <section className="space-y-3">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Disqualification reason <span className="text-red-600">*</span>
                </h4>
                <Textarea
                  value={qualReason}
                  onChange={(e) => setQualReason(e.target.value)}
                  className={!qualReason.trim() ? "border-red-300 focus-visible:ring-red-400" : ""}
                  rows={2}
                  placeholder="Required. Why is this account disqualified? (e.g. 'Acquired by X, 2024', 'Not cell therapy — small molecules only')"
                />
                {!qualReason.trim() && (
                  <p className="text-[10px] text-red-600">Required when disqualifying an account.</p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Free-form notes live in the Notes section of the read-only view — save tier first, then add notes there.
                </p>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 mt-5">
          <Button size="sm" onClick={handleSave} disabled={updateAccount.isPending}>
            {updateAccount.isPending ? "Saving..." : "Save"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// NotesSection — always-visible block inside the read-only Targeting card.
// Shows the canonical rep note + author/timestamp and an inline textarea
// with a single "Save note" action.
//
// Reps never trigger validators from the UI. Batch runs of
// rep_note_validator.py / modality_validator.py pick up edited notes
// automatically (a DB trigger clears note_verifiability on text change).
// ───────────────────────────────────────────────────────────────────────────
function NotesSection({
  company,
  userId,
}: {
  company: Record<string, any>;
  userId: string | null;
}) {
  const saveNote = useSaveAccountNote();
  const [newNote, setNewNote] = useState("");

  // notes_updated_by is a uuid FK to user_profiles; resolve the display name
  // best-effort from the already-on-row fields so we don't need an extra round trip.
  const noteAuthor =
    company.last_reviewed_by ||
    company.sales_rep ||
    null;
  const noteUpdatedAt = company.notes_updated_at
    ? new Date(company.notes_updated_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const handleSave = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) {
      toast.error("Note is empty");
      return;
    }
    try {
      await saveNote.mutateAsync({
        companyId: company.id,
        note: trimmed,
        userId,
        userName: company.last_reviewed_by || null,
        isFirstNote: !company.notes,
      });
      setNewNote("");
      toast.success("Note saved");
    } catch (e: any) {
      toast.error("Failed to save note", { description: e?.message });
    }
  };

  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
        <MessageSquare className="h-3.5 w-3.5" />
        Notes
      </h4>

      {/* Current note */}
      {company.notes ? (
        <div className="rounded-md border bg-muted/30 p-3">
          <p className="text-sm whitespace-pre-wrap">{company.notes}</p>
          {(noteAuthor || noteUpdatedAt) && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {noteAuthor && <span>{noteAuthor}</span>}
              {noteAuthor && noteUpdatedAt && <span> · </span>}
              {noteUpdatedAt && <span>{noteUpdatedAt}</span>}
            </p>
          )}
          {company.engagement_notes_migrated_at && (
            <div className="mt-2 pt-2 border-t border-dashed flex flex-wrap items-center gap-2 text-[11px]">
              <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700 bg-indigo-50">
                Engagement note migrated
              </Badge>
              <span className="text-muted-foreground">
                Structured entry lives on the Engagement tab.
              </span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">No notes yet.</p>
      )}

      {/* New-note input */}
      <div className="space-y-2">
        <Label className="text-xs">Add a note</Label>
        <Textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Capture a new note about this account."
          rows={3}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saveNote.isPending || !newNote.trim()}
          >
            {saveNote.isPending ? "Saving..." : "Save note"}
          </Button>
        </div>
        {company.last_validated_at && (
          <p className="text-[10px] text-muted-foreground">
            AI last checked{" "}
            {new Date(company.last_validated_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </p>
        )}
      </div>
    </section>
  );
}
